import type { PostgrestError } from '@supabase/supabase-js';
import {
  SESSION_RECONNECTING_CODE,
  SESSION_RECONNECTING_MESSAGE,
  hasReconnectingCode,
} from '@joshua/supabase-guard';

/**
 * Base class for all service-layer errors.
 *
 * Services throw ApiError instead of returning {data, error} tuples so that
 * TanStack Query's error path fires naturally. Business-friendly `message`
 * is always pt-BR (user-visible); `cause` carries the underlying technical
 * error for logging/telemetry.
 */
export class ApiError extends Error {
  readonly cause?: unknown;
  readonly code?: string;

  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.cause = cause;

    if (isPostgrestError(cause)) {
      this.code = cause.code ?? undefined;
    }
  }
}

/**
 * Resource was requested but not found. Maps to 404-style UX.
 * Thrown when `.single()` returns no rows or a lookup-by-id misses.
 */
export class NotFoundError extends ApiError {
  constructor(message: string, cause?: unknown) {
    super(message, cause);
    this.name = 'NotFoundError';
  }
}

/**
 * Caller lacks permission to perform the action.
 * Maps to Supabase/Postgres RLS violations (code 42501).
 */
export class PermissionError extends ApiError {
  constructor(message: string, cause?: unknown) {
    super(message, cause);
    this.name = 'PermissionError';
  }
}

/** pt-BR copy for an action refused while the session is reconnecting (JOSH-89). */
export const RECONNECTING_MESSAGE = 'Reconectando — tente salvar em instantes';

/**
 * The request was answered locally with `SESSION_RECONNECTING` by the session
 * guard (JOSH-89): the token refresh is rate-limited, so the server did not
 * run it. A pause, not a failure: queries keep their last data and retry when
 * health is `ok`; save actions keep the form open with `RECONNECTING_MESSAGE`.
 */
export class ReconnectingError extends ApiError {
  override readonly code = SESSION_RECONNECTING_CODE;

  constructor(cause?: unknown) {
    super(RECONNECTING_MESSAGE, cause);
    this.name = 'ReconnectingError';
  }
}

/**
 * True for a `ReconnectingError` or any error (or its `cause`) carrying the
 * `SESSION_RECONNECTING` code, such as the raw PostgrestError supabase-js
 * builds from the guard's local 401.
 */
export function isReconnectingError(err: unknown): boolean {
  return err instanceof ReconnectingError || hasReconnectingCode(err);
}

/**
 * Text markers of a reconnecting answer once an error was flattened to a
 * string: the pt-BR copy, the body code, and the guard's own English message
 * (pinned by `errors.reconnecting.test.ts` against a real guard response).
 */
const RECONNECTING_TEXT_MARKERS = [
  RECONNECTING_MESSAGE,
  SESSION_RECONNECTING_CODE,
  SESSION_RECONNECTING_MESSAGE,
];

/** True when user-facing text was built from a reconnecting answer. */
export function isReconnectingText(text: unknown): boolean {
  return typeof text === 'string' && RECONNECTING_TEXT_MARKERS.some((m) => text.includes(m));
}

/**
 * User input failed validation before hitting the DB (e.g., zod parse failure
 * mapped into a service error, or a guard inside the service).
 */
export class ValidationError extends ApiError {
  readonly fields?: Record<string, string[]>;

  constructor(message: string, fields?: Record<string, string[]>, cause?: unknown) {
    super(message, cause);
    this.name = 'ValidationError';
    this.fields = fields;
  }
}

/**
 * Narrow `unknown` to PostgrestError without importing any.
 * Used when normalizing Supabase errors into ApiError subclasses.
 */
export function isPostgrestError(err: unknown): err is PostgrestError {
  return (
    typeof err === 'object' &&
    err !== null &&
    'message' in err &&
    'code' in err
  );
}

/**
 * Extract a plain string message from an `unknown` error.
 *
 * Use this inside `catch (err) { ... }` blocks when you need a `string` for
 * logs, toasts built by string concatenation, or template literals. Never
 * re-widen `err` to `any` just to read `.message` — narrow through this helper.
 *
 * For structured toast rendering (title + description) with technical-error
 * translation, use `getErrorMessage` from `utils/errorMessageTranslator.ts`
 * instead — that helper is the structured counterpart to this one.
 */
export function getErrorMessageString(err: unknown): string {
  if (isReconnectingError(err)) return RECONNECTING_MESSAGE;
  if (err instanceof Error) return err.message;
  if (isPostgrestError(err)) return err.message;
  if (typeof err === 'string') return err;
  return 'Erro desconhecido';
}

/**
 * Translate a raw Supabase/Postgrest error into the most specific ApiError
 * subclass available. Services call this from their catch/error branches so
 * callers get semantic errors instead of raw DB strings.
 */
export function toApiError(message: string, cause: unknown): ApiError {
  if (cause instanceof ReconnectingError) return cause;
  if (isReconnectingError(cause)) return new ReconnectingError(cause);
  if (cause instanceof ApiError) return cause;

  if (isPostgrestError(cause)) {
    if (cause.code === 'PGRST116') {
      return new NotFoundError(message, cause);
    }
    if (cause.code === '42501') {
      return new PermissionError(message, cause);
    }
  }

  return new ApiError(message, cause);
}
